 




/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-refresh/only-export-components */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import type { ItemsWithUomBody, ItemsWithUomQueryParams, ItemWithUomRow, PagedResponse } from './itemsWithUomLookup.api';
import { fetchItemsWithUomLookup } from './itemsWithUomLookup.api';
import type { APIResponse } from '@store/Shared/shared';

// ==============================
// Cache (in-memory) - TTL 10 minutes
// ==============================
const TTL_MS = 10 * 60 * 1000;

type CacheEntry = { ts: number; value: APIResponse<PagedResponse<ItemWithUomRow>> };
const cache = new Map<string, CacheEntry>();

export const clearItemsWithUomPickerCache = () => cache.clear();

// ✅ Normalize params to avoid server errors when InventoryId is missing
const normalizeParams = (p: ItemsWithUomQueryParams): ItemsWithUomQueryParams => {
  if (!p?.InventoryId) {
    // عند عدم اختيار مخزن: اطفي WithLots لتجنب أي Endpoint/Query يعتمد على المخزن
    return { ...p, InventoryId: undefined, WithLots: false };
  }
  return p;
};

const stableKey = (params: ItemsWithUomQueryParams, body: ItemsWithUomBody) => {
  const p = normalizeParams(params);

  const keyObj = {
    p: {
      PriceListId: p.PriceListId ?? null,
      Scope: p.Scope ?? null,
      InventoryId: p.InventoryId ?? null,
      WithParent: p.WithParent ?? null,
      WithImage: p.WithImage ?? null,
      WithLots: p.WithLots ?? null,
      OnlyEnabled: p.OnlyEnabled ?? null
    },
    b: {
      pageIndex: body.pageIndex,
      pageSize: body.pageSize,
      itemCode: (body as any).itemCode ?? null,
      name: (body as any).name ?? null,
      barcode: (body as any).barcode ?? null,
      barcodeEqual: (body as any).barcodeEqual ?? null,
      categoryId: (body as any).categoryId ?? null,
      unitOfMeasure: (body as any).unitOfMeasure ?? null,
      brandId: (body as any).brandId ?? null,
      itemType: (body as any).itemType ?? null,
      purchasingEnabledFlag: (body as any).purchasingEnabledFlag ?? null,
      customerOrderEnabledFlag: (body as any).customerOrderEnabledFlag ?? null,
      itemIds: (body as any).itemIds ?? null
    }
  };
  return JSON.stringify(keyObj);
};

async function fetchWithCache(params: ItemsWithUomQueryParams, body: ItemsWithUomBody, signal?: AbortSignal) {
  const p = normalizeParams(params);
  const key = stableKey(p, body);
  const now = Date.now();
  const hit = cache.get(key);

  if (hit && now - hit.ts < TTL_MS) return hit.value;

  const data = await fetchItemsWithUomLookup(p, body, signal);
  cache.set(key, { ts: now, value: data });
  return data;
}

// ==============================
// Public Types
// ==============================
export type ItemUomPickerSearchField = 'itemCode' | 'barcode' | 'name' | 'barcodeEqual';

export type ItemUomPickerField =
  | 'id'
  | 'itemId'
  | 'categoryId'
  | 'itemCode'
  | 'name'
  | 'name2'
  | 'barcode'
  | 'isPrimaryUOM'
  | 'unitOfMeasure'
  | 'unitOfMeasureName'
  | 'unitOfMeasureName2'
  | 'tax'
  | 'itemVat_Rate'
  | 'costPrice'
  | 'quentity'
  | 'reservedQty'
  | 'authorizeNegativeStock'
  | 'unitPrice'
  | 'enabledFlag'
  | 'isParent'
  | 'image'
  | 'uomFactor'
  | 'lastCostPrice'
  | 'qtyByLot';

export type ItemUomPickerArgs = {
  title?: string;
  params: ItemsWithUomQueryParams;
  bodyDefaults?: Partial<Omit<ItemsWithUomBody, 'pageIndex' | 'pageSize'>>;

  pageSize?: number;
  searchMode?: 'manual' | 'auto';
  initialSearchField?: ItemUomPickerSearchField;

  isDisabledRow?: (row: ItemWithUomRow) => boolean;

  lotMode?: 'none' | 'optional' | 'required';

  // ✅ Columns control (Fixed + Dynamic)
  fixedFields?: ItemUomPickerField[];
  dynamicFields?: ItemUomPickerField[];
  hiddenFields?: ItemUomPickerField[];
};

export type ItemUomPickerResult = {
  row: ItemWithUomRow;
  lotId?: string | null;
  lotNumber?: string | null;
};

// ==============================
// Columns (Meta + Helpers)
// ==============================
type ColumnDef = {
  field: ItemUomPickerField;
  labelAr: string;
  labelEn: string;
  width?: number | string;
  align?: 'start' | 'center' | 'end';
};

const FIELD_DEFS: ColumnDef[] = [
  { field: 'itemCode', labelAr: 'الكود', labelEn: 'Code', width: 130 },
  { field: 'name', labelAr: 'الاسم', labelEn: 'Name' },
  { field: 'name2', labelAr: 'الاسم 2', labelEn: 'Name 2' },
  { field: 'barcode', labelAr: 'الباركود', labelEn: 'Barcode', width: 160 },

  { field: 'unitOfMeasureName', labelAr: 'الوحدة', labelEn: 'UOM', width: 170 },
  { field: 'unitOfMeasureName2', labelAr: 'الوحدة 2', labelEn: 'UOM 2', width: 170 },

  { field: 'itemVat_Rate', labelAr: 'VAT%', labelEn: 'VAT%', width: 90, align: 'center' },
  { field: 'tax', labelAr: 'Tax', labelEn: 'Tax', width: 120, align: 'end' },

  { field: 'unitPrice', labelAr: 'السعر', labelEn: 'Price', width: 120, align: 'end' },
  { field: 'costPrice', labelAr: 'التكلفة', labelEn: 'Cost', width: 120, align: 'end' },
  { field: 'lastCostPrice', labelAr: 'آخر تكلفة', labelEn: 'Last Cost', width: 120, align: 'end' },

  { field: 'uomFactor', labelAr: 'معامل الوحدة', labelEn: 'UOM Factor', width: 120, align: 'end' },
  { field: 'quentity', labelAr: 'الكمية', labelEn: 'Qty', width: 90, align: 'end' },
  { field: 'reservedQty', labelAr: 'محجوز', labelEn: 'Reserved', width: 90, align: 'end' },

  { field: 'enabledFlag', labelAr: 'مفعل', labelEn: 'Enabled', width: 90, align: 'center' },
  { field: 'isPrimaryUOM', labelAr: 'أساسية', labelEn: 'Primary', width: 90, align: 'center' },
  { field: 'isParent', labelAr: 'Parent', labelEn: 'Parent', width: 90, align: 'center' },

  { field: 'image', labelAr: 'صورة', labelEn: 'Image', width: 80, align: 'center' },
  { field: 'qtyByLot', labelAr: 'Lots', labelEn: 'Lots', width: 90, align: 'center' },

  { field: 'id', labelAr: 'ID', labelEn: 'ID', width: 220 },
  { field: 'itemId', labelAr: 'ItemId', labelEn: 'ItemId', width: 220 },
  { field: 'categoryId', labelAr: 'CategoryId', labelEn: 'CategoryId', width: 220 },
  { field: 'unitOfMeasure', labelAr: 'UOM Id', labelEn: 'UOM Id', width: 220 }
];

const defByField = new Map<ItemUomPickerField, ColumnDef>(FIELD_DEFS.map(d => [d.field, d]));

const DEFAULT_FIXED: ItemUomPickerField[] = ['itemCode', 'name', 'barcode', 'unitOfMeasureName', 'itemVat_Rate'];

type ResolvedColumn = {
  field: ItemUomPickerField;
  label: string;
  width?: number | string;
  align: 'start' | 'center' | 'end';
};

const resolveColumns = (args: ItemUomPickerArgs | null, lang: 'ar' | 'en'): ResolvedColumn[] => {
  const fixed = args?.fixedFields?.length ? args.fixedFields : DEFAULT_FIXED;
  const dynamic = args?.dynamicFields ?? [];
  const hidden = new Set(args?.hiddenFields ?? []);

  const fields = [...fixed, ...dynamic].filter(f => !hidden.has(f));

  const uniq: ItemUomPickerField[] = [];
  for (const f of fields) if (!uniq.includes(f)) uniq.push(f);

  return uniq.map(field => {
    const meta = defByField.get(field);
    return {
      field,
      label: lang === 'ar' ? meta?.labelAr ?? field : meta?.labelEn ?? field,
      width: meta?.width,
      align: meta?.align ?? 'start'
    };
  });
};

const isIdLikeField = (f: ItemUomPickerField) => ['id', 'itemId', 'categoryId', 'unitOfMeasure'].includes(f);

const formatCell = (row: any, field: ItemUomPickerField, lang: 'ar' | 'en') => {
  const v = row?.[field];

  if (field === 'name') return (lang === 'ar' ? row?.name2 || row?.name : row?.name || row?.name2) || '-';
  if (field === 'unitOfMeasureName')
    return (lang === 'ar'
      ? row?.unitOfMeasureName2 || row?.unitOfMeasureName
      : row?.unitOfMeasureName || row?.unitOfMeasureName2) || '-';

  if (field === 'name2') return row?.name2 || '-';
  if (field === 'unitOfMeasureName2') return row?.unitOfMeasureName2 || '-';

  if (field === 'itemVat_Rate') return typeof v === 'number' ? `${v}%` : '-';

  if (field === 'image') return v ? <i className='bi bi-image' /> : '-';
  if (field === 'qtyByLot') return Array.isArray(v) ? v.length : '-';

  if (typeof v === 'boolean') {
    return v ? <span className='badge bg-success'>Yes</span> : <span className='badge bg-secondary'>No</span>;
  }

  if (v === null || v === undefined || v === '') return '-';

  if (typeof v === 'object') {
    const s = JSON.stringify(v);
    return s.length > 60 ? s.slice(0, 60) + '…' : s;
  }

  return String(v);
};

// ==============================
// ✅ Stable Modal Component (خارج hook) لمنع الوميض
// ==============================
type ModalProps = {
  open: boolean;
  title: string;
  lang: 'ar' | 'en';

  // ⬅️ نفس الاسم القديم حتى لا يكسر أي استخدام
  canOpen: boolean;

  // ✅ جديد (اختياري) لعرض رسالة معلوماتية فقط
  inventorySelected?: boolean;

  loading: boolean;

  rows: ItemWithUomRow[];
  totalItems: number;
  pageIndex: number;
  hasMore: boolean;

  searchField: ItemUomPickerSearchField;
  term: string;

  selected: ItemWithUomRow | null;

  searchMode: 'manual' | 'auto';

  columns: ResolvedColumn[];

  isDisabledRow?: (row: ItemWithUomRow) => boolean;

  onConfirmRow: (row: ItemWithUomRow) => void;

  onChangeSearchField: (v: ItemUomPickerSearchField) => void;
  onChangeTerm: (v: string) => void;

  onSearch: () => void;
  onLoadMore: () => void;

  onSelectRow: (row: ItemWithUomRow) => void;
  onCancel: () => void;
  onConfirm: () => void;
};

export const ItemUomPickerModal: React.FC<ModalProps> = React.memo(function ItemUomPickerModal(props) {
  const {
    open,
    title,
    lang,
    canOpen,
    inventorySelected,
    loading,
    rows,
    totalItems,
    hasMore,
    searchField,
    term,
    selected,
    searchMode,
    columns,
    isDisabledRow,
    onChangeSearchField,
    onChangeTerm,
    onSearch,
    onLoadMore,
    onSelectRow,
    onConfirmRow,
    onCancel,
    onConfirm
  } = props;

  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      searchRef.current?.focus();
      searchRef.current?.select?.();
    }, 0);
    return () => clearTimeout(t);
  }, [open, searchField]);

  const moveSelection = (dir: 1 | -1) => {
    if (!rows?.length) return;
    const curIndex = selected
      ? rows.findIndex(r => r.itemId === selected.itemId && r.unitOfMeasure === selected.unitOfMeasure)
      : -1;
    const start = curIndex < 0 ? (dir === 1 ? -1 : rows.length) : curIndex;
    for (let step = 1; step <= rows.length; step++) {
      const idx = (start + dir * step + rows.length) % rows.length;
      const candidate = rows[idx];
      const disabled = isDisabledRow?.(candidate) ?? false;
      if (!disabled) {
        onSelectRow(candidate);
        return;
      }
    }
  };

  const handleHotkeys = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveSelection(1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveSelection(-1);
      return;
    }
    if (e.key === 'Enter') {
      if (!selected && searchMode === 'manual') return;
      e.preventDefault();
      if (selected) onConfirmRow(selected);
    }
  };

  return (
    <Modal
      show={open}
      onHide={onCancel}
      size='xl'
      centered
      backdrop='static'
      animation={false}
      onKeyDown={handleHotkeys}
      className='theme-modal'
    >
      <Modal.Header closeButton className='bg-theme bg-opacity-10 border-bottom'>
        <Modal.Title className='d-flex align-items-center'>
          <i className='bi bi-ui-checks-grid me-2'></i>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* ✅ لا نمنع الفتح عند عدم وجود مخزن - فقط رسالة معلوماتية */}
        {/* {inventorySelected === false && (
          <div className='alert alert-info mb-3'>
            <div className='d-flex align-items-center'>
              <i className='bi bi-info-circle me-2'></i>
              <div className='small'>
                {lang === 'ar'
                  ? 'لم يتم اختيار مخزن. سيتم عرض الأصناف بدون كميات/لوتس.'
                  : 'No inventory selected. Items will be shown without stock qty / lots.'}
              </div>
            </div>
          </div>
        )} */}

        {/* Search Controls */}
        <div className='d-flex gap-2 align-items-center mb-3'>
          <div className='input-group input-group-sm' style={{ width: 190 }}>
            <span className='input-group-text'>
              <i className='bi bi-search me-1'></i>
              {lang === 'ar' ? 'بحث بـ' : 'Search by'}
            </span>
            <select
              className='form-select'
              value={searchField}
              onChange={e => onChangeSearchField(e.target.value as ItemUomPickerSearchField)}
              disabled={!canOpen}
            >
              <option value='itemCode'>كود الصنف</option>
              <option value='barcode'> باركود (يحتوي) </option>
              <option value='barcodeEqual'>باركود</option>
              <option value='name'>الاسم</option>
            </select>
          </div>

          <div className='flex-grow-1'>
            <div className='input-group input-group-sm'>
              <input
                ref={searchRef as any}
                className='form-control'
                value={term}
                disabled={!canOpen}
                placeholder={lang === 'ar' ? 'ابحث...' : 'Search...'}
                onChange={e => onChangeTerm(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    if (selected) {
                      e.preventDefault();
                      onConfirmRow(selected);
                      return;
                    }
                    if (searchMode === 'manual') {
                      e.preventDefault();
                      onSearch();
                    }
                  }
                }}
              />
              {searchMode === 'manual' && (
                <button className='btn btn-theme btn-sm' type='button' disabled={!canOpen || loading} onClick={onSearch}>
                  {loading ? <span className='spinner-border spinner-border-sm' /> : <i className='bi bi-search me-1'></i>}
                  {lang === 'ar' ? 'بحث' : 'Search'}
                </button>
              )}
            </div>
          </div>

          {/* Status Badges */}
          <div className='d-flex gap-2 align-items-center ms-auto'>
            <span className='badge bg-secondary'>
              النتائج: {rows.length}/{totalItems}
            </span>
            {selected ? (
              <span className='badge bg-success'>
                <i className='bi bi-check-circle me-1'></i>
                محدد
              </span>
            ) : (
              <span className='badge bg-light text-dark'>بدون تحديد</span>
            )}
          </div>
        </div>

        {/* Table */}
        <div className='table-responsive' style={{ maxHeight: 450, overflow: 'auto' }}>
          <table className='table table-hover table-sm mb-0'>
            <thead className='position-sticky top-0 bg-theme bg-opacity-50 border-bottom'>
              <tr>
                {columns.map(c => (
                  <th
                    key={c.field}
                    style={{ width: c.width }}
                    className={c.align === 'end' ? 'text-end' : c.align === 'center' ? 'text-center' : ''}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map(r => {
                const disabled = isDisabledRow?.(r) ?? false;
                const active = selected?.itemId === r.itemId && selected?.unitOfMeasure === r.unitOfMeasure;

                return (
                  <tr
                    key={`${r.itemId}-${r.unitOfMeasure}`}
                    onClick={() => !disabled && onSelectRow(r)}
                    onDoubleClick={() => {
                      if (disabled) return;
                      onSelectRow(r);
                      onConfirmRow(r);
                    }}
                    style={{
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      background: active ? 'rgba(25,135,84,.08)' : undefined
                    }}
                    title={disabled ? 'غير مسموح / مكرر' : ''}
                  >
                    {columns.map(c => (
                      <td
                        key={c.field}
                        className={c.align === 'end' ? 'text-end' : c.align === 'center' ? 'text-center' : ''}
                        style={isIdLikeField(c.field) ? { fontSize: 12 } : undefined}
                      >
                        {formatCell(r as any, c.field, lang)}
                        {c.field === 'unitOfMeasureName' && r.isPrimaryUOM && <span className='text-warning ms-1'>⭐</span>}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {!rows.length && (
                <tr>
                  <td colSpan={Math.max(columns.length, 1)} className='text-center text-muted py-4'>
                    {loading ? (
                      <div className='d-flex align-items-center justify-content-center'>
                        <span className='spinner-border spinner-border-sm me-2'></span>
                        جاري التحميل...
                      </div>
                    ) : (
                      'لا توجد نتائج'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Actions */}
        <div className='d-flex justify-content-between align-items-center mt-3'>
          <div>
            {/* {hasMore && ( */}
            {/* {hasMore && ( */}
            {true && (
              <button className='btn btn-outline-secondary btn-sm' disabled={loading || !canOpen} onClick={onLoadMore}>
                {loading ? <span className='spinner-border spinner-border-sm me-1'></span> : <i className='bi bi-arrow-down-circle me-1'></i>}
                تحميل المزيد
              </button>
            )}
          </div>

          <div className='d-flex gap-2'>
            <button className='btn btn-secondary' onClick={onCancel}>
              <i className='bi bi-x-circle me-1'></i>
              إلغاء
            </button>
            <button className='btn btn-theme' onClick={onConfirm} disabled={!selected}>
              <i className='bi bi-check2-circle me-1'></i>
              تأكيد
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
});

// ==============================
// Hook
// ==============================
export function useItemUomPicker() {
  const resolverRef = useRef<((value: ItemUomPickerResult | null) => void) | null>(null);

  const [open, setOpen] = useState(false);
  const [args, setArgs] = useState<ItemUomPickerArgs | null>(null);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ItemWithUomRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pageIndex, setPageIndex] = useState(1);

  const [searchField, setSearchField] = useState<ItemUomPickerSearchField>('itemCode');
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState<ItemWithUomRow | null>(null);

  const skipNextAutoLoadRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const { i18n } = useTranslation();
  const lang: 'ar' | 'en' = i18n.language === 'en' ? 'en' : 'ar';

  const pageSize = args?.pageSize ?? 10;
  const searchMode = args?.searchMode ?? 'manual';
  const title = args?.title ?? (lang === 'ar' ? 'اختيار صنف ووحدة' : 'Pick Item & UOM');

  // ✅ الآن لا نمنع التحميل/البحث بدون مخزن
  const canOpen = true;

  const inventorySelected = !!args?.params?.InventoryId;

  // ✅ إذا ما فيه مخزن: نخفي أعمدة الكمية/المحجوز/اللوتس تلقائياً حتى لا تربك المستخدم
  const effectiveArgs = useMemo(() => {
    if (!args) return null;

    const extraHidden: ItemUomPickerField[] = !inventorySelected ? ['quentity', 'reservedQty', 'qtyByLot'] : [];
    const mergedHidden = Array.from(new Set([...(args.hiddenFields ?? []), ...extraHidden]));

    return { ...args, hiddenFields: mergedHidden };
  }, [args, inventorySelected]);

  const columns = useMemo(() => resolveColumns(effectiveArgs, lang), [effectiveArgs, lang]);

  const buildBody = (nextPageIndex: number): ItemsWithUomBody => {
    const clean = term.trim();

    const body: ItemsWithUomBody = {
      pageIndex: nextPageIndex, // 1-based
      pageSize,
      ...(effectiveArgs?.bodyDefaults || {})
    } as any;

    (body as any).itemCode = undefined;
    (body as any).barcode = undefined;
    (body as any).barcodeEqual = undefined;
    (body as any).name = undefined;

    if (clean) {
      if (searchField === 'itemCode') (body as any).itemCode = clean;
      if (searchField === 'barcode') (body as any).barcode = clean;
      if (searchField === 'barcodeEqual') (body as any).barcodeEqual = clean;
      if (searchField === 'name') (body as any).name = clean;
    }

    return body;
  };

  const load = async (opts: { reset: boolean; nextPageIndex: number }) => {
    if (!effectiveArgs) return;

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    try {
      const body = buildBody(opts.nextPageIndex);

      // ✅ normalize params (إذا ما فيه مخزن يطفي WithLots)
      const params = normalizeParams(effectiveArgs.params);

      const res = await fetchWithCache(params, body, abortRef.current.signal);

      if (res.succeeded === true) {
        setTotalItems(res.data.totalItems || 0);
        setPageIndex(res.data.pageIndex || opts.nextPageIndex);
        setRows(prev => (opts.reset ? res.data.data : [...prev, ...res.data.data]));
      } else {
        // handle errors if needed
      }
    } catch (e) {
      if ((e as any)?.name !== 'CanceledError') console.error(e);
      if (opts.reset) setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const pickItemUom = (nextArgs: ItemUomPickerArgs) => {
    setArgs(nextArgs);
    setSearchField(nextArgs.initialSearchField ?? 'itemCode');
    setTerm('');
    setRows([]);
    setTotalItems(0);
    setPageIndex(1);
    setSelected(null);

    setOpen(true);

    return new Promise<ItemUomPickerResult | null>(resolve => {
      resolverRef.current = resolve;
    });
  };

  const close = (result: ItemUomPickerResult | null) => {
    abortRef.current?.abort();
    setOpen(false);
    resolverRef.current?.(result);
    resolverRef.current = null;
  };

  useEffect(() => {
    if (!open) return;
    if (!effectiveArgs) return;

    // ✅ منع التحميل الثاني في auto بعد الفتح مباشرة
    skipNextAutoLoadRef.current = true;

    load({ reset: true, nextPageIndex: 1 });
  }, [open, effectiveArgs?.params?.InventoryId, effectiveArgs?.params?.PriceListId, effectiveArgs?.params?.Scope, effectiveArgs]);

  useEffect(() => {
    if (!open) return;
    if (!effectiveArgs) return;
    if (searchMode !== 'auto') return;

    if (skipNextAutoLoadRef.current) {
      skipNextAutoLoadRef.current = false;
      return;
    }

    const h = setTimeout(() => {
      load({ reset: true, nextPageIndex: 1 });
    }, 350);

    return () => clearTimeout(h);
  }, [term, searchField, open, searchMode]);

  const hasMore = useMemo(() => rows.length < totalItems, [rows.length, totalItems]);

  useEffect(() => {
    if (!open) return;
    if (selected) return;
    if (!rows.length) return;

    const first = rows.find(r => !(effectiveArgs?.isDisabledRow?.(r) ?? false));
    if (first) setSelected(first);
  }, [open, rows]);

  const modalProps: ModalProps = useMemo(
    () => ({
      open,
      title,
      lang,
      canOpen, // ✅ يبقى موجود (true دائماً) حتى لا يكسر أي استخدام
      inventorySelected,
      loading,
      rows,
      totalItems,
      pageIndex,
      hasMore,
      searchField,
      term,
      selected,
      searchMode,
      columns,
      isDisabledRow: effectiveArgs?.isDisabledRow,

      onChangeSearchField: v => setSearchField(v),
      onChangeTerm: v => setTerm(v),
      onSearch: () => load({ reset: true, nextPageIndex: 1 }),
      onLoadMore: () => load({ reset: false, nextPageIndex: pageIndex + 1 }),
      onSelectRow: r => setSelected(r),
      onConfirmRow: (row: ItemWithUomRow) => close({ row }),

      onCancel: () => close(null),
      onConfirm: () => {
        if (!selected) return;
        close({ row: selected });
      }
    }),
    [
      open,
      title,
      lang,
      canOpen,
      inventorySelected,
      loading,
      rows,
      totalItems,
      pageIndex,
      hasMore,
      searchField,
      term,
      selected,
      searchMode,
      columns,
      effectiveArgs?.isDisabledRow
    ]
  );

  return { pickItemUom, modalProps };
}

 